import {useContext, useEffect, useState} from "react";
import Spinner from "../../../Spinner";
import {authContext} from "../../../../context/auth";
import {useParams} from "react-router-dom";
import EntryRow from "./EntryRow";
import {progressContext} from "../../../../context/progress";
import filterResponse from "../../../../filterResponse";
import Pagination from "./Pagination";
import Filters from "./Filters";
import useMediaQuery from "../../../../hooks/useMediaQuery";

export default function Review({job, setLoading, setError, loading}) {
	const [entries, setEntries] = useState([]);
	const [page, setPage] = useState(0);
	const [maxPage, setMaxPage] = useState(2);
	const [quantity, setQuantity] = useState(25);
	const [isShown, setIsShown] = useState(false);
	const [filters, setFilters] = useState({
		search: '',
		qaFilter: '',
		reviewFilter: '',
		id: ''
	});

	const {id} = useParams();
	const {getProgress} = useContext(progressContext);
	const isDesktop = useMediaQuery("(min-width: 768px)");
	const {user} = useContext(authContext);

	// PM and QA reach this page through Inspect, which signs them in as the contributor. The
	// server says which of the two is looking; only they may set a verdict.
	const inspector = Boolean(user?.inspector);

	const inputs = [...job.entryInputs.booleans.map(value => {
		return {
			boolean: true,
			...value
		}
	}), ...job.entryInputs.textFields.map(value => {
		return {
			textField: true,
			...value
		}
	}),...job.entryInputs.regexFields.map(value => {
		return {
			regexField: true,
			...value
		}
	}), ...job.entryInputs.dropDowns.map(value => {
		return {
			dropDown: true,
			...value
		}
	}), ...job.entryInputs.dropDownsMultiple.map(value => {
		return {
			dropDown: true,
			multiple: true,
			...value
		}
	}), ...job.entryInputs.lists.map(value => {
		return {
			list: true,
			...value
		}
	}), ...job.entryInputs.files.map(value => {
		return {
			file: true,
			...value
		}
	})].sort((a, b) => a.name.localeCompare(b.name));

	// A search that leaves you on page 4 hides its own result: the match is usually on the first
	// page of a much shorter list.
	useEffect(() => {
		setPage(0);
	}, [quantity, filters]);

	useEffect(() => {
		const tab = document.getElementById("tab" + 3);
		let isMounted = true;

		tab.addEventListener("shown.bs.tab", onShown);
		tab.addEventListener("hidden.bs.tab", onHide);

		function onShown(){
			console.log('shown');
			isMounted && setIsShown(true);
		}
		function onHide() {
			console.log('hide');
			isMounted && setIsShown(false);
		}

		return () => {
			tab.removeEventListener("shown.bs.tab", onShown);
			tab.removeEventListener("hidden.bs.tab", onHide);
			isMounted = false;
		};
	}, [id, page]);

	useEffect(() => {
		let isMounted = true;
		const abortController = new AbortController();

		(async () => {
			if (isShown) {
				setLoading(true);
				setError(null);

				try {
					const urlParams = new URLSearchParams();
					urlParams.append("crowdJobId", id);
					urlParams.append("page", page);
					urlParams.append("quantity", quantity);

					for (const [key, value] of Object.entries(filters)) {
						if (value !== '' && value !== undefined) {
							urlParams.append(key, value);
						}
					}
					
					const response = await fetch(process.env.REACT_APP_ENDPOINT + '/crowdEntries?' + urlParams.toString(), {
						method: "GET",
						credentials: "include",
						signal: abortController.signal
					});
					await filterResponse(response);

					if (response.status === 200) {
						const {entries} = await response.json();
						isMounted && setEntries(entries);
						isMounted && await getProgress();
					}
					else {
						throw new Error("Wrong status code.");
					}
				}
				catch (e) {
					console.error(e);
					isMounted && setError("Failed to get your entries.");
				}

				isMounted && setLoading(false);
			}
		})();

		return () => {
			isMounted = false;
			abortController.abort();
		}
	}, [id, quantity, page, filters, isShown]);

	useEffect(() => {
		let isMounted = true;
		const abortController = new AbortController();

		(async () => {
			if (isShown) {
				setLoading(true);
				setError(null);

				try {
					const urlParams = new URLSearchParams();
					urlParams.append("crowdJobId", id);
					urlParams.append("quantity", quantity);

					for (const [key, value] of Object.entries(filters)) {
						if (value !== '' && value !== undefined) {
							urlParams.append(key, value);
						}
					}
					const response = await fetch(process.env.REACT_APP_ENDPOINT + '/crowdEntries/maxPage?' + urlParams.toString(), {
						method: "GET",
						credentials: "include",
						signal: abortController.signal
					});
					await filterResponse(response);

					if (response.status === 200) {
						const {maxPage} = await response.json();
						isMounted && setMaxPage(maxPage);
					}
					else {
						throw new Error("Wrong status code.");
					}
				}
				catch (e) {
					console.error(e);
					isMounted && setError("Failed to get your entries.");
				}

				isMounted && setLoading(false);
			}
		})();

		return () => {
			isMounted = false;
			abortController.abort();
		}
	}, [id, quantity, filters, isShown]);

	async function deleteEntry(entryId) {
		setEntries(prev => {
			return [...prev].filter(value => value.id !== entryId);
		});

		await getProgress();
	}

	async function editEntry(entryId, entry) {
		setEntries(prev => {
			const newEntries = [...prev];
			const index = newEntries.findIndex(value => value.id === entryId);
			newEntries[index] = entry;
			return  newEntries;
		});
		await getProgress();
	}

	// Every row carries its own Edit entry modal, holding the picks and uploads not yet saved. The
	// table reloads each time this tab is shown, so swapping the rows for a spinner while it did
	// unmounted every modal -- a contributor who stepped over to Entry collection and back found
	// their edit reset, with its uploads still running for nothing. The spinner is only for the
	// first load; after that the rows stay put and are replaced by entry id when the answer lands.
	const showSpinner = loading && entries.length === 0;

	useEffect(() => {
		if (!loading && entries.length === 0) {
			if (page > 0) {
				setPage(page - 1);
				setMaxPage(prev => prev - 1);
			}
		}
	}, [page, loading, entries]);

	return (
		<div className={"flex-fill d-flex flex-column h-100"}>
			<Filters filters={filters} setFilters={setFilters}/>
			<Pagination page={page} setPage={setPage} maxPage={maxPage} quantity={quantity} setQuantity={setQuantity}/>
			<div className={"overflow-auto flex-fill"} style={{minHeight: 0}}>
				{isDesktop ? (
					<table className={"table table-striped table-sm my-2"}>
						<caption>Collected entries</caption>
						<thead className={"table-dark"}>
						<tr>
							<th className={"position-sticky"} style={{top: 0, left: 0, width: "60px", zIndex: 3}}>#</th>
							{inputs.map(value => {
								return <th key={value.id} className={"position-sticky text-nowrap"} style={{top: 0, zIndex: 2}}>{value.name}</th>
							})}
							<th className={"position-sticky"} style={{top: 0, zIndex: 2}}>Stamp</th>
							<th className={"position-sticky text-nowrap"} style={{top: 0, zIndex: 2}}>QA errors</th>
							<th className={"position-sticky text-nowrap"} style={{top: 0, zIndex: 2}}>Review status</th>
							<th className={"position-sticky"} style={{top: 0, right: 0, width: '135px', zIndex: 3}}>Actions</th>
						</tr>
						</thead>
						<tbody>
						{showSpinner ? <tr>
								<td colSpan={inputs.length + 5} className={"text-center"}><Spinner small={true} /></td>
							</tr> :
							entries.map((value, index) => {
								return <EntryRow job={job} inputs={inputs} entry={value} key={value.id} editEntry={editEntry} deleteEntry={deleteEntry} index={index+1} quantity={quantity} page={page} inspector={inspector} highlight={filters.search} />;
							})
						}
						</tbody>
					</table>
				) : (
					<div className={"py-2"}>
						{showSpinner ? (
							<div className={"text-center py-3"}><Spinner small={true} /></div>
						) : (
							entries.map((value, index) => {
								return <EntryRow layout={"card"} job={job} inputs={inputs} entry={value} key={value.id} editEntry={editEntry} deleteEntry={deleteEntry} index={index+1} quantity={quantity} page={page} inspector={inspector} highlight={filters.search} />;
							})
						)}
					</div>
				)}
			</div>

			<Pagination page={page} setPage={setPage} maxPage={maxPage} quantity={quantity} setQuantity={setQuantity}/>
		</div>
	);
}